import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferCapacityWithAgesSelect from 'components/OfferPage/Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import TourV2DepartureList from './Tourv2DepartureList'
import TourV2DepartureMonthsCarousel from './TourV2DepartureMonthsCarousel'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TourV2DepartureYearFilters from './TourV2DepartureYearFilters'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import TourV2DeparturesByYearList from './TourV2DeparturesByYearList'
import LayoutContainer from 'components/Common/LayoutContainer'
import OfferCalendarLuxPlusToggle from 'luxPlus/components/OfferCalendarLuxPlusToggle'
import { sum, uniqueBy } from 'lib/array/arrayUtils'
import Backdrop from 'components/utils/Backdrop'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import ModalContext from 'contexts/ModalContext'
import OfferCalendarCapacityChip from 'components/OfferPage/Filters/CalendarPicker/CalendarCapacity/OfferCalendarCapacityChip'
import Group from 'components/utils/Group'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { addToCheckoutCart, initialiseCheckout } from 'actions/CheckoutActions'
import generateTourV2CheckoutItem from 'lib/tours/generateTourV2CheckoutItems'
import { getDepartureCheapestOptionForTravellers } from 'lib/tours/tourDepartureUtils'
import TourV2DepartureBookingModal from './TourV2DepartureBookingModal'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addToCartGTM, addTourToCart } from 'analytics/eventDefinitions'
import { addToCart } from 'analytics/snowplow/events'
import { fireEcommerceGTMEvent, addGTMEvent } from 'api/googleTagManager'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import GeoContext from 'contexts/geoContext'
import * as Analytics from 'analytics/analytics'
import { getFilteredDepartures } from 'selectors/TourV2/tourV2OfferPageSelectors'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import TourDepositBanner from '../TourDepositBanner'

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: ${rem(280)} 1fr ${rem(280)};
  align-items: center;
  justify-items: center;
  gap: ${rem(12)};
`

const CapacityFilter = styled(OfferCapacityWithAgesSelect)`
  width: 100%;
`

interface Props {
  offer: App.Tours.TourV2Offer;
  departureYear?: number;
  monthCarouselRef?: React.Ref<HTMLDivElement>;
  activeFilter?: OfferPageFilterType;
  selectedDeparture?: App.Tours.TourV2OfferDeparture
  allDepartures: Array<App.Tours.TourV2OfferDeparture>;
  filteredDepartures: Array<App.Tours.TourV2OfferDeparture>;
  keepBrowsing?: boolean;
}

function TourV2Departures(props: Props) {
  const {
    activeFilter,
    offer,
    departureYear,
    monthCarouselRef,
    selectedDeparture,
    allDepartures,
    filteredDepartures,
    keepBrowsing,
  } = props

  const filterRowRef = useRef<HTMLDivElement>(null)
  const showModal = useContext(ModalContext)
  const dispatch = useAppDispatch()
  const offerPageDispatch = useOfferPageDispatch()
  const { currentCurrency } = useContext(GeoContext)

  const hasMultipleYears = useMemo(() => {
    const uniqueYears = uniqueBy(allDepartures, dep => new Date(dep.startDate).getFullYear())
    return uniqueYears.length > 1
  }, [allDepartures])

  useEffect(() => {
    if (activeFilter === 'capacity') {
      filterRowRef.current?.scrollIntoView({ block: 'start' })
    } else if (selectedDeparture) {
      offerPageDispatch({ type: OfferPageActions.setTourV2Departure, departure: undefined })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter])

  const setDepartureYear = useCallback((year: number) => offerPageDispatch({ type: OfferPageActions.setDepartureYear, year }), [offerPageDispatch])

  const onMobileCapacityClick = useCallback(() => {
    showModal(<TourV2DepartureBookingModal
      offer={offer}
      defaultStep="travellers"
    />)
  }, [offer, showModal])

  const onTravellersApply = useCallback((travellers: Array<App.Occupants>) => {
    offerPageDispatch({ type: OfferPageActions.setRooms, rooms: travellers as any })
    if (selectedDeparture) {
      const items = travellers.map(traveller => {
        const option = getDepartureCheapestOptionForTravellers(selectedDeparture, traveller)
        if (option) {
          return generateTourV2CheckoutItem(
            offer,
            selectedDeparture,
            traveller,
            option,
          )
        }
      })

      if (items.every(item => !!item)) {
        if (keepBrowsing) {
          dispatch(addToCheckoutCart(items))
          offerPageDispatch({ type: OfferPageActions.setKeepBrowsing, keepBrowsing: false })
          showSnackbar('Your item has been added to the cart.', 'success', { action: { label: 'View cart', to: '/cart' } })
        } else {
          dispatch(initialiseCheckout(items))
          dispatch(navigateToCheckout())
        }

        const totalTravellers = sumUpOccupancies(travellers)
        const priceTotal = sum(items, item => item.total ?? 0)
        fireEcommerceGTMEvent(addToCartGTM(offer, currentCurrency, priceTotal, totalTravellers, 'tour'))
        addGTMEvent(addTourToCart(offer, currentCurrency, priceTotal, totalTravellers))
        Analytics.trackEvent(addToCart())
      }
    }
  }, [offerPageDispatch, selectedDeparture, dispatch, offer, currentCurrency, keepBrowsing])

  return <div>
    <CSSBreakpoint min="tablet">
      <LayoutContainer>
        <VerticalSpacer gap={32}>
          <FilterRow ref={filterRowRef}>
            <Backdrop visible={!!activeFilter} />
            <CapacityFilter
              capacities={offer.roomCapacity}
              occupantUnit="traveller"
              maxRooms={5}
              boundaryRef={filterRowRef}
              onApply={onTravellersApply}
              dropdownCtaText={selectedDeparture ? 'Continue' : 'Apply'}
            />
            {
              allDepartures.length > 1 && hasMultipleYears &&
                <TourV2DepartureYearFilters
                  departures={allDepartures}
                  departureYear={departureYear}
                  onSelect={setDepartureYear}
                />
            }
          </FilterRow>
          {
            allDepartures.length > 1 &&
              <TourV2DepartureMonthsCarousel
                departures={allDepartures}
                ref={monthCarouselRef}
              />
          }
          <TourDepositBanner offer={offer} />
          <TourV2DepartureList
            offer={offer}
            departures={filteredDepartures}
          />
        </VerticalSpacer>
      </LayoutContainer>
    </CSSBreakpoint>
    <CSSBreakpoint max="mobile">
      <VerticalSpacer gap={28}>
        <LayoutContainer>
          <VerticalSpacer gap={28}>
            <VerticalSpacer gap={12}>
              {hasMultipleYears && <HorizontalContainerWithFadedOverflow>
                <TourV2DepartureYearFilters
                  departures={allDepartures}
                  departureYear={departureYear}
                  onSelect={setDepartureYear}
                />
              </HorizontalContainerWithFadedOverflow>}
              <Group direction="horizontal" gap={8} wrap="wrap">
                {offer.luxPlus.hasMemberPrices && <OfferCalendarLuxPlusToggle />}
                <OfferCalendarCapacityChip
                  onClick={onMobileCapacityClick}
                  offer={offer}
                />
              </Group>
            </VerticalSpacer>
            <TourDepositBanner offer={offer} />
          </VerticalSpacer>
        </LayoutContainer>
        <TourV2DeparturesByYearList
          offer={offer}
          departures={filteredDepartures}
        />
      </VerticalSpacer>
    </CSSBreakpoint>
  </div>
}

type MappedProps = Pick<Props,
'offer' |
'departureYear' |
'activeFilter' |
'selectedDeparture' |
'allDepartures' |
'filteredDepartures' |
'keepBrowsing'
>

export default withOfferPageState((state): MappedProps => {
  return {
    offer: state.tourV2Offer!,
    allDepartures: state.tourV2Variation!.departures,
    departureYear: state.departureYear,
    activeFilter: state.activeFilter,
    selectedDeparture: state.tourV2Departure,
    filteredDepartures: getFilteredDepartures(state),
    keepBrowsing: state.keepBrowsing,
  }
}, TourV2Departures)
